import { useState, useEffect, useRef, Component, ErrorInfo, ReactNode } from 'react';
import { Film, CheckCircle, ChevronDown, ChevronUp, ChevronsDownUp, ChevronsUpDown, Play, Pause, Loader2, Copy, FolderOpen, Settings as SettingsIcon, Scissors } from 'lucide-react';
import { Project, Scene, SCENE_TYPE_CONFIG } from './types';
import { SceneAccordion } from './components/SceneAccordion';
import { LibraryPanel } from './components/LibraryPanel';
import { SettingsPanel } from './components/SettingsPanel';
import { ClipperPanel } from './components/ClipperPanel';

// Error Boundary to catch and display errors
class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-red-900 text-white p-8">
          <h1 className="text-2xl font-bold mb-4">Something went wrong</h1>
          <pre className="bg-black/50 p-4 rounded overflow-auto text-sm">
            {this.state.error?.message}
            {'\n\n'}
            {this.state.error?.stack}
          </pre>
          <button 
            className="mt-4 px-4 py-2 bg-white text-red-900 rounded"
            onClick={() => {
              localStorage.clear();
              window.location.href = '/';
            }}
          >
            Clear Data & Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

type TabType = 'projects' | 'library' | 'settings' | 'clipper';

// Legacy: LITTERBOX_PROXY only used to load old catbox project URLs for backward compatibility
const LITTERBOX_PROXY = 'https://litterbox-proxy.daniel-807.workers.dev';
const S3_WORKER = 'https://scene-editor-s3.daniel-807.workers.dev';
const S3_SAVE_WORKER = 'https://scene-editor-s3.daniel-807.workers.dev';

// Extract project slug from S3 URL
function extractProjectSlug(url: string): string | null {
  const match = url.match(/projects\/([^/]+)\//);
  return match ? match[1] : null;
}

// Demo project for testing
const DEMO_PROJECT: Project = {
  name: 'Demo Video Project',
  created: new Date().toISOString(),
  brand: { colors: ['#0A1629', '#1A2C2E', '#34A49F'], captionStyle: 'minimal-bottom', imageStyle: 'tech-dark-mode' },
  source: { avatarUrl: 'https://remotionlambda-useast1-2eywyfynbf.s3.us-east-1.amazonaws.com/sites/remotion-demo/avatar.mp4', transcriptPath: '' },
  scenes: [
    { id: 1, type: 'SPEAKER_ONLY', start: 0, end: 5.5, transcript: 'Hey, I just came across this LinkedIn post and I want to share something with you.', beat: 'HOOK', broll: null },
    { id: 2, type: 'SPLIT_SCREEN', start: 5.5, end: 12.2, transcript: "There's a new tool that's completely changing how we analyze competitor content.", beat: 'SETUP', broll: { prompt: 'Modern analytics dashboard, dark mode UI', source: 'generated', url: undefined } },
    { id: 3, type: 'SPLIT_SCREEN', start: 12.2, end: 20.5, transcript: 'It can pull data from LinkedIn, Instagram, TikTok - basically anywhere your competitors are posting.', beat: 'EVIDENCE', broll: { prompt: 'Social media icons connected by glowing lines', source: 'generated', url: undefined } },
    { id: 4, type: 'FULL_BLEED_BROLL', start: 20.5, end: 26, transcript: "And the best part? It uses AI to surface insights you'd never find manually.", beat: 'REVEAL', broll: { prompt: 'AI brain visualization with data flowing', source: 'generated', url: undefined } },
    { id: 5, type: 'SPEAKER_ONLY', start: 26, end: 32, transcript: 'This is going to change competitive intelligence forever.', beat: 'THESIS', broll: null },
  ],
};

function getSceneStatus(scene: Scene): 'done' | 'empty' | 'preview' | 'speaker-only' {
  if (scene.type === 'SPEAKER_ONLY' || scene.type === 'OVERLAY') return 'speaker-only';
  if (scene.broll?.url || scene.broll?.source === 'component') return 'done';
  return 'empty';
}

function App() {
  const [project, setProject] = useState<Project | null>(null);
  const [expandedSceneId, setExpandedSceneId] = useState<number | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [isApproved, setIsApproved] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [approvedUrl, setApprovedUrl] = useState<string | null>(null);
  const [videoCollapsed, setVideoCollapsed] = useState(false);
  const [videoSize, setVideoSize] = useState<'S' | 'M' | 'L'>('M');
  const [previewExpanded, setPreviewExpanded] = useState(false); // Mobile floating preview
  const [allExpanded, setAllExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [projectSlug, setProjectSlug] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('projects');
  const videoRef = useRef<HTMLVideoElement>(null);

  // Autosave to localStorage (include projectSlug)
  useEffect(() => {
    if (project && hasChanges) {
      const saveData = { project, savedAt: new Date().toISOString(), projectSlug };
      localStorage.setItem('storyboard-autosave', JSON.stringify(saveData));
      setLastSaved(new Date());
    }
  }, [project, hasChanges, projectSlug]);

  // Load project
  useEffect(() => {
    const loadProject = async () => {
      const params = new URLSearchParams(window.location.search);
      const loadUrl = params.get('load');
      
      if (!loadUrl) {
        const saved = localStorage.getItem('storyboard-autosave');
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            const { project: savedProject, savedAt, projectSlug: savedSlug } = parsed || {};
            
            // Strict validation: must have scenes array, source object, and valid structure
            const isValid = savedProject 
              && typeof savedProject === 'object'
              && Array.isArray(savedProject.scenes) 
              && savedProject.scenes.length > 0 
              && savedProject.source
              && typeof savedProject.source === 'object';
            
            if (isValid) {
              setProject(savedProject);
              setExpandedSceneId(savedProject.scenes[0]?.id || null);
              setLastSaved(new Date(savedAt));
              if (savedSlug) setProjectSlug(savedSlug);
              setLoading(false);
              return;
            } else {
              // Auto-recovery: invalid structure, clear and show demo
              console.warn('🔄 Auto-recovery: Invalid saved project structure, resetting to demo');
              localStorage.removeItem('storyboard-autosave');
              setError('Previous project had invalid data - loaded demo instead');
              setTimeout(() => setError(null), 4000); // Clear error after 4s
            }
          } catch (err) {
            // Auto-recovery: parse error, clear and show demo
            console.error('🔄 Auto-recovery: Failed to parse saved project:', err);
            localStorage.removeItem('storyboard-autosave');
            setError('Previous project was corrupted - loaded demo instead');
            setTimeout(() => setError(null), 4000);
          }
        }
        setProject(DEMO_PROJECT);
        setExpandedSceneId(2);
        setLoading(false);
        return;
      }
      
      try {
        // Extract project slug from S3 URL
        const slug = extractProjectSlug(loadUrl);
        if (slug) setProjectSlug(slug);
        
        let fetchUrl = loadUrl;
        if (loadUrl.includes('catbox.moe')) {
          fetchUrl = `${LITTERBOX_PROXY}?url=${encodeURIComponent(loadUrl)}`;
        } else if (loadUrl.includes('s3.') || loadUrl.includes('amazonaws.com')) {
          // Proxy S3 URLs through our worker to add CORS headers
          fetchUrl = `${S3_WORKER}/get-project?url=${encodeURIComponent(loadUrl)}`;
        }
        const response = await fetch(fetchUrl);
        const loadedProject = await response.json() as Project;
        
        // Validate loaded project has required fields
        if (!loadedProject || !Array.isArray(loadedProject.scenes) || loadedProject.scenes.length === 0) {
          throw new Error('Invalid project structure');
        }
        
        setProject(loadedProject);
        setExpandedSceneId(loadedProject.scenes[0]?.id || null);
        localStorage.setItem('storyboard-autosave', JSON.stringify({ project: loadedProject, savedAt: new Date().toISOString() }));
        setLastSaved(new Date());
      } catch (err) {
        console.error('Failed to load project:', err);
        setError('Failed to load project');
        setProject(DEMO_PROJECT);
        setExpandedSceneId(2);
      } finally {
        setLoading(false);
      }
    };
    
    loadProject();
  }, []);

  // Video time update
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const handleTimeUpdate = () => setCurrentTime(video.currentTime);
    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => video.removeEventListener('timeupdate', handleTimeUpdate);
  }, []);

  // Play/pause sync
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (isPlaying) video.play();
    else video.pause();
  }, [isPlaying]);

  const totalDuration = project?.scenes?.[project.scenes?.length - 1]?.end || 0;
  const progress = totalDuration > 0 ? (currentTime / totalDuration) * 100 : 0;

  const getProgressStats = () => {
    if (!project) return { total: 0, ready: 0, needsWork: 0 };
    const scenes = project?.scenes || [];
    const incomplete = scenes.filter(s => {
      const cfg = SCENE_TYPE_CONFIG[s.type] || SCENE_TYPE_CONFIG.SPEAKER_ONLY;
      if (!cfg.brollRequired) return false;
      return !s.broll?.url && s.broll?.source !== 'component';
    });
    return { total: scenes.length, ready: scenes.length - incomplete.length, needsWork: incomplete.length };
  };

  const stats = getProgressStats();

  const handleSceneUpdate = (updatedScene: Scene) => {
    if (!project || !Array.isArray(project.scenes)) return;
    setProject({ ...project, scenes: project.scenes.map(s => s.id === updatedScene.id ? updatedScene : s) });
    setHasChanges(true);
  };

  const handleLoadProject = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const text = await file.text();
      const loadedProject = JSON.parse(text) as Project;
      setProject(loadedProject);
      setExpandedSceneId(loadedProject.scenes[0]?.id || null);
      setHasChanges(false);
    };
    input.click();
  };

  const handleSaveProject = async () => {
    if (!project) return;
    
    // Always save to S3 - generate slug if needed
    const slug = projectSlug || project.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') + '-' + Date.now().toString(36);
    
    setIsSaving(true);
    try {
      const response = await fetch(S3_SAVE_WORKER, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, projectSlug: slug, storyboard: project }),
      });
      const data = await response.json();
      if (data.success) {
        setHasChanges(false);
        setLastSaved(new Date());
        setProjectSlug(slug); // Remember slug for future saves
        console.log('Saved to S3:', data.url);
        // Sync to project index
        fetch(`${S3_SAVE_WORKER}/update-project`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            slug,
            name: project.name,
            avatarUrl: project.source?.avatarUrl,
            sceneCount: project.scenes?.length,
            duration: project.scenes?.[project.scenes.length - 1]?.end,
          }),
        }).catch(() => {}); // fire and forget
      } else {
        alert('Save failed: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Save error:', err);
      alert('Save failed. Check console for details.');
    } finally {
      setIsSaving(false);
    }
  };

  // Pre-render validation
  const validateProject = (proj: Project): { valid: boolean; warnings: string[]; errors: string[] } => {
    const warnings: string[] = [];
    const errors: string[] = [];
    const blockedDomains = ['litter.catbox.moe', 'files.catbox.moe'];
    const brollRequiredTypes = ['SPLIT_SCREEN', 'CIRCLE_PIP', 'FULL_BROLL'];
    
    // Check avatar URL
    if (!proj.source?.avatarUrl) {
      errors.push('Missing avatar URL');
    } else if (blockedDomains.some(d => proj.source.avatarUrl.includes(d))) {
      warnings.push('Avatar URL uses catbox (may timeout in Lambda). Consider migrating to S3.');
    }
    
    // Check each scene
    proj.scenes.forEach((scene, i) => {
      const sceneNum = i + 1;
      
      // Check if b-roll is required but missing (component type doesn't need URL)
      const hasValidBroll = scene.broll?.url || scene.broll?.source === 'component' || (scene.broll?.source === 'lottie' && scene.broll?.url);
      if (brollRequiredTypes.includes(scene.type) && !hasValidBroll) {
        errors.push(`Scene ${sceneNum}: ${scene.type} requires b-roll`);
      }
      
      // Check for blocked domains in b-roll
      if (scene.broll?.url && blockedDomains.some(d => scene.broll!.url!.includes(d))) {
        warnings.push(`Scene ${sceneNum}: B-roll uses catbox (may timeout). Consider re-uploading.`);
      }
    });
    
    return { valid: errors.length === 0, warnings, errors };
  };
  
  const handleApprove = async () => {
    if (!project) return;
    
    // Run validation
    const validation = validateProject(project);
    if (!validation.valid) {
      alert('Cannot approve:\n\n' + validation.errors.join('\n'));
      return;
    }
    if (validation.warnings.length > 0) {
      const proceed = confirm('Warnings:\n\n' + validation.warnings.join('\n') + '\n\nProceed anyway?');
      if (!proceed) return;
    }
    
    setIsApproving(true);
    
    try {
      // Generate slug from project name or use existing
      const slug = projectSlug || project.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') + '-' + Date.now().toString(36);
      
      const response = await fetch(S3_WORKER, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, projectSlug: slug, storyboard: project }),
      });
      
      const data = await response.json();
      if (data.url) {
        setApprovedUrl(data.url);
        setIsApproved(true);
        setHasChanges(false);
        setProjectSlug(slug);
        localStorage.setItem('lastApprovedStoryboard', data.url);
        localStorage.setItem('storyboard-autosave', JSON.stringify({ project, savedAt: new Date().toISOString(), slug }));
        // Sync to project index
        fetch(`${S3_WORKER}/update-project`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            slug,
            name: project.name,
            avatarUrl: project.source?.avatarUrl,
            sceneCount: project.scenes?.length,
            duration: project.scenes?.[project.scenes.length - 1]?.end,
          }),
        }).catch(() => {}); // fire and forget
      }
    } catch (err) {
      console.error('Approve failed:', err);
      alert('Failed to upload. Try again.');
    } finally {
      setIsApproving(false);
    }
  };

  const toggleScene = (sceneId: number) => {
    setExpandedSceneId(expandedSceneId === sceneId ? null : sceneId);
  };

  const seekToScene = (scene: Scene) => {
    if (videoRef.current) {
      videoRef.current.currentTime = scene.start;
      setCurrentTime(scene.start);
    }
  };

  const handleScrub = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!videoRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const time = percent * totalDuration;
    videoRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const handleMergeScenes = (sceneIndex: number) => {
    if (!project || !Array.isArray(project.scenes) || sceneIndex >= project.scenes.length - 1) return;
    const currentScene = project.scenes[sceneIndex];
    const nextScene = project.scenes[sceneIndex + 1];
    const mergedScene: Scene = {
      ...currentScene,
      end: nextScene.end,
      transcript: `${currentScene.transcript} ${nextScene.transcript}`,
      broll: currentScene.broll?.url ? currentScene.broll : nextScene.broll,
    };
    const newScenes = [...project.scenes.slice(0, sceneIndex), mergedScene, ...project.scenes.slice(sceneIndex + 2)].map((s, i) => ({ ...s, id: i + 1 }));
    setProject({ ...project, scenes: newScenes });
    setHasChanges(true);
  };

  const handleSplitScene = (sceneIndex: number, splitWordIndex: number) => {
    if (!project || !Array.isArray(project.scenes)) return;
    const scene = project.scenes[sceneIndex];
    const words = scene.transcript.split(' ');
    const totalDur = scene.end - scene.start;
    const splitRatio = splitWordIndex / words.length;
    const splitTime = scene.start + (totalDur * splitRatio);
    
    const firstScene: Scene = { ...scene, end: splitTime, transcript: words.slice(0, splitWordIndex).join(' ') };
    const secondScene: Scene = { ...scene, id: scene.id + 1, start: splitTime, transcript: words.slice(splitWordIndex).join(' '), broll: null };
    const newScenes = [...project.scenes.slice(0, sceneIndex), firstScene, secondScene, ...project.scenes.slice(sceneIndex + 1)].map((s, i) => ({ ...s, id: i + 1 }));
    setProject({ ...project, scenes: newScenes });
    setHasChanges(true);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Film className="w-16 h-16 text-green-600 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-500">Loading project...</p>
        </div>
      </div>
    );
  }

  if (!project) return null;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('projects')}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'projects'
                  ? 'text-green-600 border-green-600'
                  : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Film className="w-4 h-4" />
              Projects
            </button>
            <button
              onClick={() => setActiveTab('library')}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'library'
                  ? 'text-green-600 border-green-600'
                  : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FolderOpen className="w-4 h-4" />
              Library
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'settings'
                  ? 'text-green-600 border-green-600'
                  : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <SettingsIcon className="w-4 h-4" />
              Settings
            </button>
            <button
              onClick={() => setActiveTab('clipper')}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'clipper'
                  ? 'text-green-600 border-green-600'
                  : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Scissors className="w-4 h-4" />
              Clipper
            </button>
          </div>
        </div>
      </div>
      
      {/* Library Tab */}
      {activeTab === 'library' && (
        <LibraryPanel
          currentSlug={projectSlug}
          currentProject={project}
          onLoadVersion={(loadedProject, slug) => {
            setProject(loadedProject);
            setExpandedSceneId(loadedProject.scenes[0]?.id || null);
            setHasChanges(false);
            setProjectSlug(slug);
            setActiveTab('projects');

            // Update browser URL so refresh works
            const storyboardUrl = `https://remotionlambda-useast1-2eywyfynbf.s3.us-east-1.amazonaws.com/projects/${slug}/storyboard.json`;
            window.history.pushState({}, '', `?load=${encodeURIComponent(storyboardUrl)}`);

            localStorage.setItem('storyboard-autosave', JSON.stringify({
              project: loadedProject,
              savedAt: new Date().toISOString(),
              projectSlug: slug,
            }));
            setLastSaved(new Date());
          }}
        />
      )}
      
      {/* Settings Tab */}
      {activeTab === 'settings' && <SettingsPanel />}

      {/* Clipper Tab */}
      {activeTab === 'clipper' && <ClipperPanel />}

      {/* Projects Tab */}
      {activeTab === 'projects' && (
        <>
      {/* Sticky Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-2 sticky top-12 z-40">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-sm font-semibold text-gray-900">{project.name}</h1>
            <span className="text-xs text-gray-400">
              <span className="font-medium text-green-600">{stats.ready}</span>/{stats.total} ready
            </span>
            {stats.needsWork > 0 && <span className="text-xs text-red-500">{stats.needsWork} need b-roll</span>}
            {lastSaved && <span className="text-xs text-gray-400 hidden sm:inline">Saved {lastSaved.toLocaleTimeString()}</span>}
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => allExpanded ? setAllExpanded(false) : setAllExpanded(true)} className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors">
              {allExpanded ? <ChevronsDownUp className="w-4 h-4" /> : <ChevronsUpDown className="w-4 h-4" />}
            </button>
            <button onClick={handleLoadProject} className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded">Load</button>
            <button onClick={handleSaveProject} disabled={isSaving} className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 flex items-center gap-1">
              {isSaving ? <><Loader2 className="w-3 h-3 animate-spin" /> Saving...</> : 'Save'}
            </button>
            {isApproved && approvedUrl ? (
              <div className="flex items-center gap-1">
                <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                <button onClick={() => navigator.clipboard.writeText(approvedUrl)} className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded flex items-center gap-1">
                  <Copy className="w-3 h-3" /> Copy
                </button>
              </div>
            ) : (
              <button onClick={handleApprove} disabled={isApproving} className="px-2 py-1 text-xs text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded flex items-center gap-1">
                {isApproving ? <><Loader2 className="w-3 h-3 animate-spin" /> Uploading...</> : 'Approve'}
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto w-full p-4">
        {/* Collapsible Video Preview - Desktop only */}
        {project.source.avatarUrl && (
          <div className="hidden md:block bg-white rounded-lg border border-gray-200 overflow-hidden mb-4">
            <div className="px-3 py-2 flex items-center justify-between hover:bg-gray-50">
              <button onClick={() => setVideoCollapsed(!videoCollapsed)} className="flex items-center gap-2">
                {videoCollapsed ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronUp className="w-4 h-4 text-gray-400" />}
                <span className="text-sm font-medium text-gray-700">Video</span>
              </button>
              {!videoCollapsed && (
                <div className="flex items-center gap-0.5 bg-gray-100 rounded p-0.5">
                  {(['S', 'M', 'L'] as const).map((size) => (
                    <button key={size} onClick={() => setVideoSize(size)} className={`px-2 py-0.5 text-xs rounded transition-colors ${videoSize === size ? 'bg-white shadow text-gray-900 font-medium' : 'text-gray-500 hover:text-gray-700'}`}>{size}</button>
                  ))}
                </div>
              )}
            </div>

            {!videoCollapsed && (
              <>
                <div className="bg-gray-900 relative mx-auto transition-all" style={{ maxWidth: videoSize === 'S' ? '280px' : videoSize === 'M' ? '480px' : '100%' }}>
                  <div className="aspect-video relative">
                    <video ref={videoRef} className="w-full h-full object-contain" src={project?.source?.avatarUrl || ''} muted onEnded={() => setIsPlaying(false)} />
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-700">
                      <div className="h-full bg-green-500 transition-all" style={{ width: `${progress}%` }} />
                    </div>
                  </div>
                </div>

                <div className="px-3 py-2 border-t border-gray-200 flex items-center gap-2">
                  <button onClick={() => setIsPlaying(!isPlaying)} className="p-1.5 hover:bg-gray-100 rounded-full transition-colors">
                    {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </button>
                  <span className="text-xs text-gray-500 w-20">{formatTime(currentTime)} / {formatTime(totalDuration)}</span>
                  <div className="flex-1 h-1.5 bg-gray-200 rounded-full cursor-pointer relative" onClick={handleScrub}>
                    <div className="h-full bg-green-500 rounded-full" style={{ width: `${progress}%` }} />
                    {project.scenes?.map((scene) => (
                      <div key={scene.id} className="absolute top-0 w-0.5 h-full bg-gray-400" style={{ left: `${(scene.start / totalDuration) * 100}%` }} />
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Scene List */}
        <div className="space-y-2">
          {project.scenes?.map((scene, index) => (
            <SceneAccordion
              key={scene.id}
              scene={scene}
              isExpanded={allExpanded || expandedSceneId === scene.id}
              onToggle={() => {
                if (allExpanded) { setAllExpanded(false); setExpandedSceneId(scene.id); }
                else toggleScene(scene.id);
              }}
              onUpdate={handleSceneUpdate}
              onSeek={() => seekToScene(scene)}
              status={getSceneStatus(scene)}
              onMergeNext={() => handleMergeScenes(index)}
              onSplit={(splitWordIndex) => handleSplitScene(index, splitWordIndex)}
              isLastScene={index === (project.scenes?.length ?? 1) - 1}
              avatarUrl={project.source?.avatarUrl || ''}
            />
          ))}
        </div>
      </main>
        </>
      )}
    </div>
  );
}

function AppWithErrorBoundary() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}

export default AppWithErrorBoundary;
